import { Link } from 'react-router-dom';
import { dateTime, humanise, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Chips, StatusBadge } from '../components/ui';

interface BookingRow {
  id: string;
  status: string;
  scheduledAt: string | null;
  acceptedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  customer: { id: string; mobile: string; displayName: string | null };
  provider: { id: string; name: string };
  rating: { stars: number; comment: string | null } | null;
}

export function BookingsPage() {
  const { get, set } = useListParams();
  const status = get('status');

  const columns: Array<Column<BookingRow>> = [
    {
      key: 'customer',
      header: 'Customer',
      render: (r) => (
        <Link to={`/users/${r.customer.id}`}>{r.customer.displayName ?? fmtMobile(r.customer.mobile)}</Link>
      ),
    },
    { key: 'provider', header: 'Provider', render: (r) => <span className="cell-strong">{r.provider.name}</span> },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge value={r.status} /> },
    {
      key: 'rating',
      header: 'Rating',
      render: (r) =>
        r.rating ? (
          // FR-SRCH-03: customers never see this text; admin and the provider do.
          <span title={r.rating.comment ?? undefined}>
            {'★'.repeat(r.rating.stars)}
            <span className="cell-muted">{'★'.repeat(5 - r.rating.stars)}</span>
          </span>
        ) : (
          <span className="cell-muted">—</span>
        ),
    },
    { key: 'created', header: 'Requested', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
    { key: 'closed', header: 'Closed', render: (r) => <span className="cell-muted">{dateTime(r.closedAt)}</span> },
  ];

  return (
    <DataTable<BookingRow>
      queryKey={['bookings', status]}
      path="/admin/bookings"
      params={{ status }}
      columns={columns}
      emptyIcon="✆"
      emptyTitle="No bookings match"
      toolbar={
        <Chips
          value={status}
          onChange={(v) => set('status', v)}
          options={[
            { value: '', label: 'All' },
            ...['REQUESTED', 'ACCEPTED', 'IN_PROGRESS', 'CLOSED', 'CANCELLED'].map((v) => ({
              value: v,
              label: humanise(v),
            })),
          ]}
        />
      }
    />
  );
}
